import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { searchRestaurants } from '../services/RestaurantService';
import { Restaurant } from '../models/Restaurant';
import LocationInput from './LocationInput';
import PriceFilter from './PriceFilter';
import CuisineFilter from './CuisineFilter';
import FortuneWheel from './FortuneWheel';
import WinnerModal from './WinnerModal';
import LoadingSpinner from './LoadingSpinner';

const Home: React.FC = () => {
  // User input state
  const [location, setLocation] = useState('');
  const [radius, setRadius] = useState(1); // miles
  const [selectedPrices, setSelectedPrices] = useState<string[]>(['1', '2']);
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  
  // Restaurant data state
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Wheel and winner state
  const [isWheelVisible, setIsWheelVisible] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [isWinnerModalOpen, setIsWinnerModalOpen] = useState(false);
  
  // Effect to handle body scrolling when modals are open
  useEffect(() => {
    if (isWheelVisible || isWinnerModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isWheelVisible, isWinnerModalOpen]);

  // Handle search submission
  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!location.trim()) {
      setError('Please enter a location');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setIsWheelVisible(false);
    setSelectedRestaurant(null);
    
    try {
      // Call the restaurant search API
      const results = await searchRestaurants({
        location,
        radius,
        price: selectedPrices.join(','),
        categories: selectedCuisines.join(','),
        limit: 20,
        open_now: true,
      });
      
      if (results.length === 0) {
        setError('No restaurants found. Try different criteria.');
        setRestaurants([]);
      } else {
        setRestaurants(results);
        setIsWheelVisible(true);
      }
    } catch (err) {
      setError('Error searching for restaurants. Please try again.');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle wheel spin completion
  const handleSpinEnd = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setIsWinnerModalOpen(true);
  };
  
  // Handle spinning the wheel
  const handleSpinClick = () => {
    if (restaurants.length === 0) return;
  };
  
  return (
    <Container>
      <Header>
        <AppTitle>Dinner Spinner</AppTitle>
        <Tagline>Spin to decide where to eat!</Tagline>
      </Header>
      
      <Card>
        <form onSubmit={handleSearch}>
          <LocationInput 
            location={location}
            onLocationChange={setLocation}
            radius={radius}
            onRadiusChange={setRadius}
          />
          
          <PriceFilter 
            selectedPrices={selectedPrices}
            onChange={setSelectedPrices}
          />
          
          <CuisineFilter 
            selectedCuisines={selectedCuisines}
            onChange={setSelectedCuisines}
          />
          
          <SearchButton type="submit" disabled={isLoading}>
            {isLoading ? 'Searching...' : 'Find Restaurants'}
          </SearchButton>
        </form>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Card>
      
      <AnimatePresence>
        {isLoading && (
          <WheelModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <WheelSection
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ delay: 0.1, type: 'spring', damping: 25 }}
            >
              <LoadingSpinner message="Finding restaurants..." />
            </WheelSection>
          </WheelModal>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {isWheelVisible && restaurants.length > 0 && (
          <WheelModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <WheelSection
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ delay: 0.2, type: 'spring', damping: 25 }}
            >
              <CloseButton onClick={() => setIsWheelVisible(false)}>×</CloseButton>
              <WheelTitle>Spin to decide!</WheelTitle>
              <WheelSubtitle>{restaurants.length} restaurants found</WheelSubtitle>
              
              <FortuneWheel 
                restaurants={restaurants}
                onSpinEnd={handleSpinEnd}
                isSpinning={isSpinning}
                setIsSpinning={setIsSpinning}
              />
            </WheelSection>
          </WheelModal>
        )}
      </AnimatePresence>
      
      <WinnerModal 
        restaurant={selectedRestaurant}
        isOpen={isWinnerModalOpen}
        onClose={() => setIsWinnerModalOpen(false)}
      />
    </Container>
  );
};

const Container = styled.div`
  max-width: 1000px;
  width: 90%;
  margin: 0 auto;
  padding: 1rem 1rem 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  
  @media (min-width: 1200px) {
    max-width: 1100px;
  }
  
  @media (max-height: 700px) {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 1rem;
  
  @media (max-height: 700px) {
    margin-bottom: 0.5rem;
  }
`;

const AppTitle = styled.h1`
  font-size: 2.2rem;
  margin: 0;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-height: 700px) {
    font-size: 1.8rem;
  }
`;

const Tagline = styled.p`
  font-size: 1.1rem;
  color: var(--text-color);
  opacity: 0.8;
  
  @media (max-height: 700px) {
    font-size: 0.9rem;
  }
`;

const Card = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  
  @media (max-height: 700px) {
    padding: 1rem;
    margin-bottom: 0.5rem;
  }
`;

const SearchButton = styled.button`
  width: 100%;
  padding: 0.8rem;
  background: linear-gradient(to right, var(--primary-color), var(--accent-color));
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 12px rgba(255, 90, 95, 0.2);
  
  @media (max-height: 700px) {
    padding: 0.6rem;
    font-size: 0.9rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 90, 95, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  text-align: center;
  margin-top: 1rem;
  padding: 0.5rem;
  font-weight: 500;
`;

const WheelModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 100;
  padding: 0.5rem;
  overflow: hidden;
`;

const WheelSection = styled(motion.div)`
  position: relative;
  text-align: center;
  padding: 1.5rem;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 650px;
  width: 90%;
  max-height: 90vh;
  height: auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (min-width: 1200px) {
    max-width: 700px;
  }
  
  @media (max-height: 700px) {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.2);
  color: var(--text-color);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  line-height: 1;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const WheelTitle = styled.h2`
  font-size: 1.7rem;
  margin-bottom: 0.3rem;
  color: var(--text-color);
  
  @media (max-height: 700px) {
    font-size: 1.5rem;
    margin-bottom: 0.2rem;
  }
`;

const WheelSubtitle = styled.p`
  font-size: 1rem;
  color: var(--text-color);
  opacity: 0.7;
  margin-bottom: 1rem;
  
  @media (max-height: 700px) {
    font-size: 0.9rem;
    margin-bottom: 0.7rem;
  }
`;

export default Home;